// ✅ src/pages/VehicleV2/TyreMasterReport.jsx
// UPDATED FULL FILE
//
// ✅ Updated pattern similar to OwnVehicleReport
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / reset / pagination / print flow
// ✅ branch/status dependent dropdown refresh handled safely
// ✅ avoids infinite API loop

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tyreMasterReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const raw = toList(arr).map((x) => {
    if (typeof x === "string") {
      return { label: x, value: x, raw: x };
    }

    const label = toStr(
      x.label ??
        x.name ??
        x.text ??
        x.fld_branchname ??
        x.fld_vehicletypename ??
        x.fld_tyresize ??
        x.fld_tyremodel ??
        x.fld_tyreserialno ??
        x.fld_suppliername ??
        x.value ??
        fallbackLabel,
      fallbackLabel
    );

    const value = toStr(
      x.value ??
        x.id ??
        x.key ??
        x.fld_branchid ??
        x.fld_vehicletypeindex ??
        x.fld_id ??
        x.label ??
        fallbackLabel,
      fallbackLabel
    );

    return { label, value, ...x };
  });

  const hasSelect = raw.some((x) => toStr(x.value).toLowerCase() === "select");

  if (!raw.length) {
    return [{ label: fallbackLabel, value: fallbackLabel }];
  }

  return hasSelect
    ? raw
    : [{ label: fallbackLabel, value: fallbackLabel }, ...raw];
};

const defaultFiltersByRole = (branchType, branchId) =>
  toStr(branchType).toUpperCase() !== "HO"
    ? {
        fromDate: "",
        toDate: "",
        branch: String(branchId),
        vehicleCode: "",
        vehicleName: "",
        regNo: "",
        tyreModel: "Select",
        tyreSize: "Select",
        tyreSerial: "Select",
        tyreMake: "",
        tyreStatus: "Select",
        supplier: "Select",
        vehicleMainType: "Select",
      }
    : {
        fromDate: "",
        toDate: "",
        branch: "Select",
        vehicleCode: "",
        vehicleName: "",
        regNo: "",
        tyreModel: "Select",
        tyreSize: "Select",
        tyreSerial: "Select",
        tyreMake: "",
        tyreStatus: "Select",
        supplier: "Select",
        vehicleMainType: "Select",
      };

const fmtDate = (v) => {
  if (!v) return "";
  return toStr(v);
};

export default function TyreMasterReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);
  const [tyreType, setTyreType] = useState("new"); // new | retread

  /* ===========================
     dropdown states
  =========================== */
  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    tyreSizes: [{ label: "Select", value: "Select" }],
    tyreModels: [{ label: "Select", value: "Select" }],
    tyreSerials: [{ label: "Select", value: "Select" }],
    suppliers: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    statuses: [{ label: "Select", value: "Select" }],
  });

  /* ===========================
     filters + list states
  =========================== */
  const baseDefaultFilters = useMemo(
    () => defaultFiltersByRole(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const [filters, setFilters] = useState(baseDefaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  /* ===========================
     computed
  =========================== */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const inputHeadClass = useMemo(() => `${buttonClass} text-white`, [buttonClass]);

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  /* ===========================
     dropdown load
  =========================== */
  const loadInit = useCallback(
    async (nextFilters = filters, nextTyreType = tyreType) => {
      try {
        setDropdownLoading(true);

        const params = {
          branchId: session.branchId,
          branchType: session.branchType,
          tyreType: nextTyreType,
          branch:
            toStr(session.branchType).toUpperCase() === "HO"
              ? nextFilters.branch
              : String(session.branchId),
          status: isSelect(nextFilters.tyreStatus)
            ? "Select"
            : nextFilters.tyreStatus,
        };

        const { data } = await axios.get(`${API_BASE}/init`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load dropdowns");
        }

        setDd({
          branches: normalizeOptionList(data.branches, "Select"),
          tyreSizes: normalizeOptionList(data.tyreSizes, "Select"),
          tyreModels: normalizeOptionList(data.tyreModels, "Select"),
          tyreSerials: normalizeOptionList(data.tyreSerials, "Select"),
          suppliers: normalizeOptionList(data.suppliers, "Select"),
          vehicleTypes: normalizeOptionList(data.vehicleTypes, "Select"),
          statuses: normalizeOptionList(data.statuses, "Select"),
        });
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load dropdowns"
        );
        setDd({
          branches: [{ label: "Select", value: "Select" }],
          tyreSizes: [{ label: "Select", value: "Select" }],
          tyreModels: [{ label: "Select", value: "Select" }],
          tyreSerials: [{ label: "Select", value: "Select" }],
          suppliers: [{ label: "Select", value: "Select" }],
          vehicleTypes: [{ label: "Select", value: "Select" }],
          statuses: [{ label: "Select", value: "Select" }],
        });
      } finally {
        setDropdownLoading(false);
      }
    },
    [filters, tyreType, session, commonHeaders]
  );

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (opts = {}, nextFilters = filters, nextTyreType = tyreType) => {
      try {
        setListLoading(true);

        const targetPage = opts.page ?? page;

        const params = {
          branchId: session.branchId,
          branchType: session.branchType,
          loginUserId: session.loginUserId,
          tyreType: nextTyreType,

          fromDate: nextFilters.fromDate,
          toDate: nextFilters.toDate,

          branch:
            toStr(session.branchType).toUpperCase() === "HO"
              ? isSelect(nextFilters.branch)
                ? "Select"
                : nextFilters.branch
              : String(session.branchId),

          vehicleCode: nextFilters.vehicleCode,
          vehicleName: nextFilters.vehicleName,
          regNo: nextFilters.regNo,

          tyreModel: isSelect(nextFilters.tyreModel)
            ? "Select"
            : nextFilters.tyreModel,
          tyreSize: isSelect(nextFilters.tyreSize)
            ? "Select"
            : nextFilters.tyreSize,
          tyreSerial: isSelect(nextFilters.tyreSerial)
            ? "Select"
            : nextFilters.tyreSerial,

          tyreMake: nextFilters.tyreMake,
          tyreStatus: isSelect(nextFilters.tyreStatus)
            ? "Select"
            : nextFilters.tyreStatus,
          supplier: isSelect(nextFilters.supplier)
            ? "Select"
            : nextFilters.supplier,
          vehicleMainType: isSelect(nextFilters.vehicleMainType)
            ? "Select"
            : nextFilters.vehicleMainType,

          page: targetPage,
          pageSize,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        setRows(toList(data.rows));
        setPage(toInt(data.page, targetPage));
        setTotal(toInt(data.total, 0));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load report"
        );
        setRows([]);
        setTotal(0);
      } finally {
        setListLoading(false);
      }
    },
    [filters, tyreType, page, pageSize, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(
    async (nextTyreType = tyreType) => {
      try {
        setPageLoading(true);

        const reset =
          defaultFiltersByRole(session.branchType, session.branchId);

        setFilters(reset);
        setRows([]);
        setTotal(0);
        setPage(1);

        await loadInit(reset, nextTyreType);
      } finally {
        setPageLoading(false);
      }
    },
    [tyreType, session.branchType, session.branchId, loadInit]
  );

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBranchChange = useCallback(
    async (e) => {
      const value = e.target.value;

      const next = {
        ...filters,
        branch: value,
        tyreSize: "Select",
        tyreModel: "Select",
        tyreSerial: "Select",
        supplier: "Select",
      };

      setFilters(next);
      await loadInit(next, tyreType);
    },
    [filters, tyreType, loadInit]
  );

  const handleStatusChange = useCallback(
    async (e) => {
      const value = e.target.value;

      const next = {
        ...filters,
        tyreStatus: value,
        tyreSerial: "Select",
      };

      setFilters(next);
      await loadInit(next, tyreType);
    },
    [filters, tyreType, loadInit]
  );

  const handleSearch = useCallback(async () => {
    await fetchList({ page: 1 }, filters, tyreType);
    toast.success("Search completed");
  }, [fetchList, filters, tyreType]);

  const handleView = useCallback(async () => {
    const reset =
      defaultFiltersByRole(session.branchType, session.branchId);

    setFilters(reset);
    await loadInit(reset, tyreType);
    await fetchList({ page: 1 }, reset, tyreType);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, tyreType, loadInit, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage(tyreType);
  }, [loadPage, tyreType]);

  const handleReset = useCallback(() => {
    const reset =
      defaultFiltersByRole(session.branchType, session.branchId);

    setFilters(reset);
    setRows([]);
    setTotal(0);
    setPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId]);

  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      vehicleCode: "",
      vehicleName: "",
      regNo: "",
      tyreMake: "",
    }));
    toast.info("Cleared text filters");
  }, []);

  const handleTyreTypeChange = useCallback(
    async (value) => {
      if (value === tyreType) return;

      setTyreType(value);
      const reset =
        defaultFiltersByRole(session.branchType, session.branchId);

      setFilters(reset);
      setRows([]);
      setTotal(0);
      setPage(1);

      await loadInit(reset, value);
    },
    [tyreType, session.branchType, session.branchId, loadInit]
  );

  const handlePrintRow = useCallback(
    (row) => {
      const tyreId =
        row?.fld_tyreid ||
        row?.TyreId ||
        row?.fld_newtyre_id ||
        row?.fld_Retrededtyre_id ||
        row?.fld_tyremasid ||
        row?.id;

      if (!tyreId) {
        toast.error("Tyre ID not found in row");
        return;
      }

      navigate("/vehicle/TyreMasterReport_Print", {
        state: {
          tyreType,
          tyreId,
        },
      });
    },
    [navigate, tyreType]
  );

  const handlePrint = useCallback(() => {
    navigate("/vehicle/TyreMasterReport_AllPrint", {
      state: {
        tyreType,
        filters,
        page,
        pageSize,
        total,
      },
    });
  }, [navigate, tyreType, filters, page, pageSize, total]);

  const handleExport = useCallback(() => {
    toast.success("Export triggered (implement CSV if needed)");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goPage = useCallback(
    async (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      await fetchList({ page: next }, filters, tyreType);
    },
    [fetchList, filters, tyreType, totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage(tyreType);
  }, [loadPage, tyreType]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE MASTER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Master Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {total}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-purple-900 text-purple-100" : "bg-purple-100 text-purple-800"
                  }`}
                >
                  Type: {tyreType === "new" ? "New Tyre" : "Retread Tyre"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Tyre Type */}
              <div className="flex gap-6 mb-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={tyreType === "new"}
                    onChange={() => handleTyreTypeChange("new")}
                    className="text-blue-600 focus:ring-blue-500"
                    disabled={isBusy}
                  />
                  New Tyre
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={tyreType === "retread"}
                    onChange={() => handleTyreTypeChange("retread")}
                    className="text-blue-600 focus:ring-blue-500"
                    disabled={isBusy}
                  />
                  Retread Tyre
                </label>
              </div>

              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branch"
                        value={filters.branch}
                        onChange={handleBranchChange}
                        className={inputClass}
                        disabled={toStr(session.branchType).toUpperCase() !== "HO" || isBusy}
                      >
                        {dd.branches.map((b, idx) => (
                          <option key={`${b.value}-${idx}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        type="text"
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle name"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input
                        type="text"
                        name="regNo"
                        value={filters.regNo}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter registration number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Model</label>
                      <select
                        name="tyreModel"
                        value={filters.tyreModel}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.tyreModels.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Size</label>
                      <select
                        name="tyreSize"
                        value={filters.tyreSize}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.tyreSizes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Serial No</label>
                      <select
                        name="tyreSerial"
                        value={filters.tyreSerial}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.tyreSerials.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Make</label>
                      <input
                        type="text"
                        name="tyreMake"
                        value={filters.tyreMake}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter tyre make"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Status</label>
                      <select
                        name="tyreStatus"
                        value={filters.tyreStatus}
                        onChange={handleStatusChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.statuses.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Supplier</label>
                      <select
                        name="supplier"
                        value={filters.supplier}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.suppliers.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        name="vehicleMainType"
                        value={filters.vehicleMainType}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {dd.vehicleTypes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleClearFilters}
                        type="button"
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        Clear Text Filters
                      </button>

                      <button
                        onClick={handleReset}
                        type="button"
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleView}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handlePrint}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Tyre Records Found
                    </h3>
                    <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Try adjusting your filters or click "View All".
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={inputHeadClass}>
                        <tr>
                          <th className="px-4 py-4 text-center font-semibold">S.No</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                          <th className="px-4 py-4 text-center font-semibold">Tyre Size</th>
                          <th className="px-4 py-4 text-center font-semibold">Tyre Serial</th>
                          <th className="px-4 py-4 text-center font-semibold">Tyre Model</th>
                          <th className="px-4 py-4 text-center font-semibold">Tyre Make</th>
                          <th className="px-4 py-4 text-center font-semibold">Issued Branch</th>
                          <th className="px-4 py-4 text-center font-semibold">Current Branch</th>
                          <th className="px-4 py-4 text-center font-semibold">Issue Date</th>
                          <th className="px-4 py-4 text-center font-semibold">Supplier</th>
                          <th className="px-4 py-4 text-center font-semibold">Print</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map((r, idx) => (
                          <tr
                            key={r.fld_tyreid ?? r.TyreId ?? r.id ?? idx}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {(page - 1) * pageSize + idx + 1}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.VehicleType ??
                                r.fld_vehicletypename ??
                                r.fld_vehiclemaintype ??
                                r.VehicleMainType ??
                                ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.vehicleCode ?? r.fld_vehicle_code ?? r.VehicleCode ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.vehicleName ?? r.fld_vehicle_name ?? r.VehicleName ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.regNo ?? r.fld_vehicle_regno ?? r.VehicleRegNo ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.tyreSize ?? r.fld_tyresize ?? r.TyreSize ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.tyreSerial ?? r.fld_tyreserialno ?? r.TyreSerial ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.tyreModel ?? r.fld_tyremodel ?? r.TyreModel ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.tyreMake ?? r.fld_tyremake ?? r.TyreMake ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.issuedBranch ?? r.fld_issuebranch ?? r.IssuedBranch ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.currentBranch ?? r.fld_branchname ?? r.CurrentBranch ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {fmtDate(r.issueDate ?? r.fld_issueddate ?? r.IssueDate ?? "")}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.supplier ?? r.fld_purchasefrom ?? r.Supplier ?? ""}
                            </td>

                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => handlePrintRow(r)}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                                title="Print"
                              >
                                <FaPrint />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Page {page} of {totalPages} — Showing {(page - 1) * pageSize + 1} to{" "}
                    {Math.min(page * pageSize, total)} of {total} entries
                  </div>

                  <nav className="flex gap-1 flex-wrap justify-center">
                    <button
                      onClick={() => goPage(page - 1)}
                      disabled={isBusy || page === 1}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        page === 1 || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        onClick={() => goPage(p)}
                        disabled={isBusy}
                        type="button"
                        className={`px-3 py-1 border rounded text-sm ${
                          page === p
                            ? `${buttonClass} text-white`
                            : isDark
                            ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-100"
                        } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      onClick={() => goPage(page + 1)}
                      disabled={isBusy || page === totalPages}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        page === totalPages || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}